// Meta tags configuration for JobSeekers Page
export const metaConfig = {
  // Home page (handled in HomeLayout)
  home: {
    title: 'Home',
    description: 'JobSeekers Page - Your trusted partner for job search and recruitment. Find the perfect job or hire the best talent with our comprehensive job portal.',
    keywords: 'job search, employment, career, hiring, recruitment, job portal, job seekers, employers, job opportunities',
    ogTitle: 'JobSeekers Page - Find Your Dream Job or Hire the Best Talent',
    ogDescription: 'Connect job seekers with employers. Advanced job search, resume builder, and applicant tracking system.',
  },

  // About Us page
  aboutUs: {
    title: 'About Us',
    description: 'Learn about JobSeekers Page - a team of experienced recruitment professionals dedicated to making job search easier, faster, and more efficient for everyone.',
    keywords: 'about jobseekers page, recruitment professionals, job search platform, hiring solutions, career opportunities',
    ogTitle: 'About JobSeekers Page - Our Mission and Values',
    ogDescription: 'Discover how our experienced team is revolutionizing the job search process for both job seekers and employers.',
  },

  // Services page
  services: {
    title: 'Services',
    description: 'Explore our comprehensive services for job seekers and employers including job posting, resume search, applicant tracking, and career development tools.',
    keywords: 'job posting services, resume search, applicant tracking, career development, hiring solutions, recruitment services',
    ogTitle: 'JobSeekers Page Services - Complete Hiring Solutions',
    ogDescription: 'Comprehensive services for job seekers and employers including job posting, resume search, and applicant tracking.',
  },

  // Contact Us page
  contactUs: {
    title: 'Contact Us',
    description: 'Get in touch with JobSeekers Page team. We\'re here to help with your job search or hiring needs. Contact us for support and inquiries.',
    keywords: 'contact jobseekers page, customer support, job search help, hiring assistance, recruitment support',
    ogTitle: 'Contact JobSeekers Page - Get Support and Assistance',
    ogDescription: 'Reach out to our team for job search assistance, hiring support, or general inquiries.',
  },

  // FAQs page
  faqs: {
    title: 'Frequently Asked Questions',
    description: 'Find answers to commonly asked questions about JobSeekers Page services, job search, hiring, and platform features.',
    keywords: 'jobseekers page faq, frequently asked questions, job search help, hiring questions, platform support',
    ogTitle: 'JobSeekers Page FAQ - Common Questions Answered',
    ogDescription: 'Get answers to frequently asked questions about our job search and hiring platform.',
  },

  // Privacy Policy page
  privacyPolicy: {
    title: 'Privacy Policy',
    description: 'Read JobSeekers Page privacy policy to understand how we collect, use, and protect your personal information and data.',
    keywords: 'privacy policy, data protection, personal information, jobseekers page privacy, user data',
    ogTitle: 'JobSeekers Page Privacy Policy - Data Protection',
    ogDescription: 'Learn how we protect your privacy and handle your personal information on our platform.',
  },

  // Pricing page
  pricing: {
    title: 'Pricing Pacakages',
    description: 'Explore JobSeekers Page pricing plans for employers and job seekers. Choose the perfect plan for your hiring or job search needs.',
    keywords: 'jobseekers page pricing, hiring plans, job search plans, recruitment pricing, subscription plans',
    ogTitle: 'JobSeekers Page Pricing - Choose Your Plan',
    ogDescription: 'Find the perfect pricing plan for your hiring needs or job search requirements.',
  },

  // Team page
  team: {
    title: 'Our Team',
    description: 'Meet the experienced professionals behind JobSeekers Page who are dedicated to revolutionizing the job search and recruitment industry.',
    keywords: 'jobseekers page team, recruitment professionals, company team, hiring experts, career specialists',
    ogTitle: 'Meet the JobSeekers Page Team - Recruitment Experts',
    ogDescription: 'Get to know our team of experienced recruitment professionals committed to your success.',
  },

  // Blogs page
  blogs: {
    title: 'Blog',
    description: 'Read the latest articles, career tips, hiring insights, and industry news from JobSeekers Page blog. Find expert advice on job search, career development, and hiring strategies.',
    keywords: 'job search blog, career tips, hiring insights, recruitment blog, industry news, career advice, employment blog, job search tips',
    ogTitle: 'JobSeekers Page Blog - Career Tips and Hiring Insights',
    ogDescription: 'Stay updated with the latest career advice, hiring tips, and industry insights from our expert team.',
    ogImage: '/assets/images/blog/blog-listing.jpg',
  },

  // Blog Details page
  blogDetails: {
    title: 'Blog Post',
    description: 'Read detailed articles about job search strategies, career development, hiring best practices, and industry insights. Expert advice for job seekers and employers.',
    keywords: 'job search articles, career development, hiring best practices, recruitment insights, industry analysis, employment tips',
    ogTitle: 'JobSeekers Page Blog - In-depth Career and Hiring Articles',
    ogDescription: 'Comprehensive articles on job search, career development, and hiring strategies from industry experts.',
  },

  // Jobs page
  jobs: {
    title: 'Job Listings',
    description: 'Browse thousands of job opportunities across different industries and locations. Find your dream job with advanced search and filtering options.',
    keywords: 'job listings, job opportunities, job search, employment, career opportunities, job vacancies',
    ogTitle: 'Job Listings - Find Your Dream Job',
    ogDescription: 'Explore thousands of job opportunities with advanced search and filtering options.',
  },

  // Job Categories page
  jobCategories: {
    title: 'Job Categories',
    description: 'Browse jobs by category and industry. Find opportunities in technology, healthcare, finance, marketing, and more specialized fields.',
    keywords: 'job categories, industry jobs, technology jobs, healthcare jobs, finance jobs, marketing jobs',
    ogTitle: 'Job Categories - Browse by Industry',
    ogDescription: 'Find jobs organized by industry and category for easier job search.',
  },

  // Job Details page
  jobDetails: {
    title: 'Job Details',
    description: 'View detailed job information including requirements, responsibilities, benefits, and company details. Apply directly through our platform.',
    keywords: 'job details, job requirements, job responsibilities, company information, job application',
    ogTitle: 'Job Details - Complete Job Information',
    ogDescription: 'Get comprehensive job details and apply directly through our platform.',
  },

  // Company Details page
  companyDetails: {
    title: 'Company Profile',
    description: 'Learn about companies, their culture, values, and job opportunities. Find the perfect employer that matches your career goals.',
    keywords: 'company profile, employer information, company culture, company values, employer details',
    ogTitle: 'Company Profile - Learn About Employers',
    ogDescription: 'Discover company culture, values, and job opportunities from potential employers.',
  },

  // CMS Pages (generic)
  cmsPage: {
    title: 'Page',
    description: 'Informative content and resources from JobSeekers Page. Stay updated with the latest information about our platform and services.',
    keywords: 'jobseekers page information, platform resources, helpful content, career resources, job search help',
    ogTitle: 'JobSeekers Page - Informative Content and Resources',
    ogDescription: 'Access helpful content and resources about our job search and hiring platform.',
    ogImage: '/assets/images/webvi-logo.png',
  },
};

// Helper function to get meta config by route
export const getMetaConfig = (routeKey, customData = {}) => {
  const baseConfig = metaConfig[routeKey] || metaConfig.cmsPage;
  
  return {
    ...baseConfig,
    ...customData,
    // Generate dynamic title if custom data is provided
    title: customData.title || baseConfig.title,
    description: customData.description || baseConfig.description,
    keywords: customData.keywords || baseConfig.keywords,
  };
}; 